// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { CustomColorThemesContext } from "../../contexts/CustomColorThemesContext";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import API from "../../core/api";
import { getGuestAuthSettings } from "../../core/utils";
import Page from "../../pages/website/Page";
import { DEFAULT_COLORWAY } from "../../themes/constants";
import StyledTopBar from "../../website/components/StyledTopBar";
import { applyCustomColorOverrides } from "../../website/components/applyCustomColorOverrides";
import { convertDekuraToFancyClothes } from "../../website/components/convertDekuraToFancyClothes";
import { css } from "@emotion/react";
import { Button, DesignProvider, Input } from "@minted/fancyclothes";
import { styleUtils, theme, Divider } from "@minted/minted-components";
import merge from "lodash/merge";
import React, { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const style = {
  alert: {
    backgroundColor: "#f7e0e0",
    border: "1px solid #e6c0c3",
    borderRadius: "4px",
    color: "#af4150",
    marginBottom: "30px",
    padding: "20px 0",
  },
  containerDivider: css`
    display: flex;
    justify-content: center;
    margin-top: ${theme.spacing.x2};
    margin-bottom: ${theme.spacing.x8};
  `,
  divider: css`
    width: 15%;
    ${theme.media.lessThan(theme.breakpoints.medium)(`
      width: 30%;
    `)}
  `,
  submitPasswordWrapper: css`
    align-self: center;
  `,
  wrapper: css`
    width: 100%;
    margin: 0 auto;
    text-align: center;
    ${theme.media.greaterThan(theme.breakpoints.small)(`
      width: 500px;
      `)}
  `,
};

const bottomTextContainerStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: ${theme.spacing.x12};
`;

const bottomTextStyles = css`
  margin-bottom: ${theme.spacing.x12};
  width: 100%;
  text-align: center;
  ${theme.media.greaterThan(theme.breakpoints.small)(`
    width: 500px;
    `)}
`;

const loginContainerStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  padding: 0 ${styleUtils.rem(15)};
`;

const loginContainerStylesOnlineInvitation = css`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 100vh;
  padding: 0 ${styleUtils.rem(15)};
  ${theme.media.greaterThan(theme.breakpoints.small)(`
    justify-content: center;
  `)}
`;

const inputContainerStyles = css`
  margin-bottom: ${theme.spacing.x5};
  input {
    font-size: 16px;
  }
`;

const cookieMessageStyles = css`
  font-size: 20px;
  text-align: center;
  color: ${theme.colors.negative800};
`;

const disabledTextBoxStyle = css`
  ${inputContainerStyles}
  input {
    background-color: ${theme.colors.gray300};
  }
`;

type LoginProps = {
  colorway: string;
  finalDesign: any;
  site: any;
};

const Login: React.FC<LoginProps> = ({ colorway, finalDesign, site }) => {
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password, setPassword] = useState<string>("");

  const isOnlineInvitation = site?.isOnlineInvitation || false;

  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSubmitting(true);

    API.post("sites/authenticate/", getGuestAuthSettings(), { password })
      .then(() => {
        // Extract 'next' parameter from URL query string
        const searchParams = new URLSearchParams(location.search);
        // searchParams.get() already decodes the value; decoding again could turn
        // encoded input into an unsafe URL.
        const next = searchParams.get("next");

        // Only follow same-site paths: reject absolute URLs, protocol-relative
        // `//host` (and its `/\host` spelling), and non-HTTP schemes.
        const isSafeNext =
          next &&
          next.startsWith("/") &&
          !next.startsWith("//") &&
          !next.startsWith("/\\");

        // After successful authentication, redirect to the next page or home.
        // A full page load (not router navigation) is needed so the server
        // re-renders window.__GUEST_AUTHENTICATED__ with the new auth cookie.
        // next is validated above as a same-site path (semgrep can't see through the guard).
        // nosemgrep: javascript.browser.security.open-redirect.js-open-redirect
        window.location.href = isSafeNext ? next : "/";
      })
      .catch((error: any) => {
        let message;

        if (error.response?.status === 400) {
          message = "The provided password was incorrect";
        } else {
          message = "There was a problem logging in, please try again";
        }

        setError(message);
        setIsSubmitting(false);
        setPassword("");

        if (error.response?.status !== 400) {
          throw error;
        }
      });
  };

  const isDisabled =
    isSubmitting ||
    (typeof window !== "undefined" && !window.navigator.cookieEnabled);

  return (
    <DesignProvider colorway={colorway} design={finalDesign}>
      <div
        css={
          isOnlineInvitation
            ? loginContainerStylesOnlineInvitation
            : loginContainerStyles
        }
      >
        <Page id="login" pageData={{}} title="Please type in your password">
          {isSubmitting ? <StyledTopBar /> : null}
          <div css={style.submitPasswordWrapper}>
            {typeof window !== "undefined" &&
              !window.navigator.cookieEnabled && (
                <div css={cookieMessageStyles}>
                  Cookies are required to view this site. Please enable cookies
                  in your browser.
                </div>
              )}
            <div css={style.containerDivider}>
              <div css={style.divider}>
                <Divider type="light" />
              </div>
            </div>
            <div css={style.wrapper}>
              <form onSubmit={handleOnSubmit}>
                {error && <div css={style.alert}>{error}</div>}
                <div
                  css={isDisabled ? disabledTextBoxStyle : inputContainerStyles}
                  data-cy="websitePasswordInput"
                >
                  <Input
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    disabled={isDisabled}
                    onChange={(event) =>
                      setPassword(event.target.value.toLowerCase())
                    }
                    spellCheck={false}
                    value={password}
                  />
                </div>
                <div data-cy="websitePasswordSubmit">
                  <Button
                    block={true}
                    color="dark"
                    disabled={isDisabled}
                    onClick={() => false}
                    text="Submit"
                    type="primary"
                  />
                </div>
              </form>
            </div>
          </div>
          <div css={bottomTextContainerStyles}>
            <div css={bottomTextStyles}>
              If you don&apos;t know the password, please ask the host for it.
            </div>
          </div>
        </Page>
      </div>
    </DesignProvider>
  );
};

const LoginWrapper = (props: any) => {
  const { customColorThemes } = useContext(CustomColorThemesContext);
  const site = useCurrentSite();
  const { colorway, design } = useSelector((state: any) => ({
    colorway: site.themeId
      ? DEFAULT_COLORWAY
      : state.theme.colorwayCodeById[site.mintedProductColorChoiceId],
    design: state.theme.fancyClothesData,
  }));
  const typographyOverrides = convertDekuraToFancyClothes(
    site.themeDataOverrides || {},
  );
  const mergedDesign = merge({}, design, typographyOverrides);

  const colorTheme = customColorThemes.find(
    (theme) => theme.id === site.customColorTheme,
  );
  const finalDesign = colorTheme
    ? applyCustomColorOverrides(mergedDesign, colorway, colorTheme)
    : mergedDesign;

  return (
    <Login
      {...props}
      colorway={colorway}
      finalDesign={finalDesign}
      site={site}
    />
  );
};

export default LoginWrapper;
